import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, App, Descriptions, Select, Skeleton, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleFilled,
  CloseCircleOutlined,
  FileProtectOutlined,
  MinusCircleOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import {
  formatMicros,
  getWiring,
  listContracts,
  listLicenses,
  listRatingRuns,
  type ContractView,
  type LicenseView,
  type RatingView,
  type WiringView,
} from '@/api/commercial';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import { EmptyState } from '@/components/EmptyState';
import { InfoTip } from '@/help/InfoTip';
import { Term } from '@/help/Term';
import { formatAbsolute } from '@/lib/time';

const { Title, Paragraph, Text } = Typography;

/** Contract/license/rating status → a tag that never relies on colour alone. */
function StatusTag({ value }: { value: string | null | undefined }) {
  if (!value) return <Text type="secondary">—</Text>;
  const color =
    value === 'ACTIVE' || value === 'SEALED' || value === 'WIRED'
      ? 'green'
      : value === 'DRAFT'
        ? 'blue'
        : value === 'SUSPENDED' || value === 'INCOMPLETE'
          ? 'orange'
          : 'default';
  return <Tag color={color}>{value}</Tag>;
}

/** A boolean wiring fact with glyph + text (colour never the sole signal). */
function WiringFact({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12.5,
        color: ok ? 'var(--ol-positive)' : 'var(--ol-text-tertiary)',
      }}
    >
      {ok ? <CheckCircleFilled aria-hidden /> : <CloseCircleOutlined aria-hidden />}
      {label}
      {ok ? '' : ' — missing'}
    </span>
  );
}

interface UnitRow {
  unitKey: string;
  ratingMethod: string;
  rateMicros: number;
  tiers: number;
  consumed: number | null;
  meterSource: string | null;
}

/**
 * "My license" (OPSFL-IAMUI-1) — the hub's commercial facts, read out of the OL-COMM-01 spine
 * (/v1/commercial, RATIFIED commercial-api.v1.yaml). READ-ONLY by design: commercials are recorded
 * through the admin API by the implementation team; the "no billing, no ship" gate
 * (CommercialShipGate) enforces the wiring at go-live — this panel just makes those facts legible.
 * Honesty rules: no spine rows → say so plainly; a metering unit with no rated run shows "—",
 * never a fabricated zero; every amount is server-computed (micros passthrough).
 */
export function LicensePanel() {
  const { message } = App.useApp();
  const [contracts, setContracts] = useState<ContractView[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [licenses, setLicenses] = useState<LicenseView[] | null>(null);
  const [ratings, setRatings] = useState<RatingView[] | null>(null);
  const [wiring, setWiring] = useState<WiringView | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    void (async () => {
      setLoading(true);
      try {
        const list = await listContracts();
        if (!alive) return;
        setContracts(list);
        setSelectedId((cur) => cur ?? list[0]?.contractId ?? null);
      } catch (e) {
        if (!alive) return;
        const n = normalizeApiError(e);
        if (n.httpStatus === 403) setForbidden(true);
        else if (isNotAvailable(n)) setUnavailable(true);
        else message.error(n.message);
        setContracts(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // message stable from App context
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDetail = useCallback(
    async (contractId: string) => {
      setDetailLoading(true);
      setLicenses(null);
      setRatings(null);
      setWiring(null);
      // Each detail read degrades independently — a failed wiring read must not blank the licenses.
      const [lic, rat, wir] = await Promise.allSettled([
        listLicenses(contractId),
        listRatingRuns(contractId),
        getWiring(contractId),
      ]);
      if (lic.status === 'fulfilled') setLicenses(lic.value);
      if (rat.status === 'fulfilled') setRatings(rat.value);
      if (wir.status === 'fulfilled') setWiring(wir.value);
      const failed = [lic, rat, wir].filter((r) => r.status === 'rejected');
      if (failed.length) {
        const n = normalizeApiError((failed[0] as PromiseRejectedResult).reason);
        if (!isNotAvailable(n) && n.httpStatus !== 403) message.error(n.message);
      }
      setDetailLoading(false);
    },
    [message],
  );

  useEffect(() => {
    if (selectedId) void loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const contract = useMemo(
    () => contracts?.find((c) => c.contractId === selectedId) ?? null,
    [contracts, selectedId],
  );

  /** Latest rating run (server order preserved; fall back to ratedAt sort defensively). */
  const latestRating = useMemo(() => {
    if (!ratings?.length) return null;
    return [...ratings].sort((a, b) => (b.ratedAt ?? '').localeCompare(a.ratedAt ?? ''))[0];
  }, [ratings]);

  /** Rate card joined with the latest rated consumption — dash (never 0) where nothing was rated. */
  const unitRows = useMemo<UnitRow[]>(() => {
    if (!contract?.rates) return [];
    return contract.rates.map((r) => ({
      unitKey: r.unitKey,
      ratingMethod: r.ratingMethod,
      rateMicros: r.rateMicros,
      tiers: r.tiers?.length ?? 0,
      consumed: latestRating?.metered?.[r.unitKey] ?? null,
      meterSource: latestRating?.meterSources?.[r.unitKey] ?? null,
    }));
  }, [contract, latestRating]);

  const unitColumns: ColumnsType<UnitRow> = useMemo(
    () => [
      {
        title: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            Metering unit <InfoTip surface="admin" field="Metering unit" />
          </span>
        ),
        dataIndex: 'unitKey',
        key: 'unitKey',
        render: (v: string) => <span className="ol-mono">{v}</span>,
      },
      {
        title: 'Method',
        dataIndex: 'ratingMethod',
        key: 'ratingMethod',
        width: 110,
        render: (v: string, r) => (
          <span>
            {v}
            {r.tiers > 0 && (
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>
                {r.tiers} band{r.tiers > 1 ? 's' : ''}
              </Text>
            )}
          </span>
        ),
      },
      {
        title: 'Contracted rate',
        dataIndex: 'rateMicros',
        key: 'rateMicros',
        align: 'right',
        width: 150,
        render: (v: number, r) => (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {r.ratingMethod === 'TIERED' ? 'per band' : formatMicros(v, contract?.currency)}
          </span>
        ),
      },
      {
        title: 'Consumed (latest rated period)',
        dataIndex: 'consumed',
        key: 'consumed',
        align: 'right',
        width: 210,
        render: (v: number | null, r) =>
          v == null ? (
            <Text type="secondary" title="No rated period covers this unit yet — not zero, unrated.">
              —
            </Text>
          ) : (
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {v.toLocaleString()}
              {r.meterSource && (
                <Text type="secondary" style={{ fontSize: 10.5, marginLeft: 6 }} className="ol-mono">
                  {r.meterSource}
                </Text>
              )}
            </span>
          ),
      },
    ],
    [contract],
  );

  const licenseColumns: ColumnsType<LicenseView> = [
    {
      title: 'License',
      dataIndex: 'licenseId',
      key: 'licenseId',
      render: (v: string) => <span className="ol-mono">{v}</span>,
    },
    { title: 'Type', dataIndex: 'licenseType', key: 'licenseType', width: 130 },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (v: string) => <StatusTag value={v} />,
    },
    {
      title: 'Valid',
      key: 'valid',
      width: 210,
      render: (_, r) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {r.validFrom ? formatAbsolute(r.validFrom) : '—'} → {r.validTo ? formatAbsolute(r.validTo) : '—'}
        </Text>
      ),
    },
    {
      title: 'Entitlements',
      dataIndex: 'entitlements',
      key: 'entitlements',
      render: (v: Record<string, unknown> | null) => {
        const entries = v ? Object.entries(v) : [];
        if (!entries.length) return <Text type="secondary">—</Text>;
        return (
          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
            {entries.map(([k, val]) => (
              <Tag key={k} style={{ margin: 0, fontSize: 11 }} className="ol-mono">
                {k}: {typeof val === 'object' ? JSON.stringify(val) : String(val)}
              </Tag>
            ))}
          </span>
        );
      },
    },
    {
      title: 'Signature',
      key: 'signature',
      width: 170,
      render: (_, r) =>
        r.signature ? (
          <Text type="secondary" style={{ fontSize: 11 }} className="ol-mono">
            {r.signatureAlgorithm ?? '?'} · {r.signatureKeyId ?? '?'}
          </Text>
        ) : (
          <Text type="secondary">unsigned</Text>
        ),
    },
  ];

  const ratingColumns: ColumnsType<RatingView> = [
    {
      title: 'Period',
      key: 'period',
      render: (_, r) => (
        <Text style={{ fontSize: 12 }}>
          {r.periodStart ? formatAbsolute(r.periodStart) : '—'} →{' '}
          {r.periodEnd ? formatAbsolute(r.periodEnd) : '—'}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v: string) => <StatusTag value={v} />,
    },
    {
      title: 'Rated total',
      dataIndex: 'totalMicros',
      key: 'totalMicros',
      align: 'right',
      width: 160,
      render: (v: number) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatMicros(v, contract?.currency)}</span>
      ),
    },
    {
      title: 'Rated at',
      dataIndex: 'ratedAt',
      key: 'ratedAt',
      width: 170,
      render: (v: string | null) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {v ? formatAbsolute(v) : '—'}
        </Text>
      ),
    },
  ];

  if (loading) return <Skeleton active paragraph={{ rows: 6 }} />;

  if (forbidden) {
    return (
      <Alert
        type="info"
        showIcon
        message="Your role can't read the commercial spine"
        description={
          <span>
            Commercial facts are gated by the <Text code>commercial.read</Text> permission key, held
            by <Text code>hub_admin</Text> and <Text code>auditor</Text> — see the Permission matrix
            tab. This screen hides nothing extra: the server refused the read.
          </span>
        }
      />
    );
  }

  if (unavailable) {
    return (
      <EmptyState
        icon={<FileProtectOutlined />}
        title="My license"
        description="The hub's commercial facts — contract identity, metering-unit rate bindings, rated usage, the signed license and the billing-wiring evidence — land here once the commercial spine (OL-COMM-01) is enabled in this deployment."
        bullets={[
          'Contract commercials and the rate card (metering-unit bindings)',
          'Rated usage per sealed/dry-run period — quantities and server-computed totals',
          'Signed license artifacts and their entitlements',
          'The billing-wiring evidence the go-live gate reads (WIRED / INCOMPLETE)',
        ]}
      />
    );
  }

  if (!contracts || contracts.length === 0) {
    return (
      <EmptyState
        icon={<FileProtectOutlined />}
        title="No commercial contract recorded for this hub"
        description={
          'The commercial spine is live, but no contract commercials have been recorded for this hub yet — so there is no license, rate card, or rated usage to show. This is a true empty, not a failure: contracts are recorded by the implementation team through the commercial admin API, and the "no billing, no ship" gate will refuse a customer go-live until the wiring evidence is complete.'
        }
      />
    );
  }

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <Title level={5} style={{ margin: 0 }}>
            My license
          </Title>
          <Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: 13, maxWidth: 760 }}>
            The hub&apos;s <Term id="commercial-contract">commercial contract</Term>,{' '}
            <Term id="rate-card">rate card</Term>, <Term id="license">license</Term> and{' '}
            <Term id="billing-wiring">billing wiring</Term> — read-only facts from the commercial
            spine. Quantities and totals are computed server-side; prices are never computed here.
          </Paragraph>
        </div>
        {contracts.length > 1 && (
          <label style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)' }}>
            <div style={{ marginBottom: 3 }}>Contract</div>
            <Select
              value={selectedId ?? undefined}
              onChange={(v) => setSelectedId(v)}
              options={contracts.map((c) => ({
                value: c.contractId,
                label: `${c.contractId} (${c.status})`,
              }))}
              style={{ minWidth: 220 }}
              aria-label="Select contract"
            />
          </label>
        )}
      </div>

      {contract && (
        <>
          <Descriptions
            size="small"
            column={{ xs: 1, sm: 2, md: 3 }}
            bordered
            style={{ marginBottom: 16 }}
            items={[
              {
                key: 'id',
                label: (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    Contract <InfoTip surface="admin" field="Contract" />
                  </span>
                ),
                children: <span className="ol-mono">{contract.contractId}</span>,
              },
              { key: 'status', label: 'Status', children: <StatusTag value={contract.status} /> },
              { key: 'currency', label: 'Currency', children: contract.currency ?? '—' },
              {
                key: 'platformFee',
                label: 'Platform fee',
                children: (
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatMicros(contract.platformFeeMicros, contract.currency)}
                  </span>
                ),
              },
              {
                key: 'valid',
                label: 'Validity',
                children: (
                  <span style={{ fontSize: 12.5 }}>
                    {contract.validFrom ? formatAbsolute(contract.validFrom) : '—'} →{' '}
                    {contract.validTo ? formatAbsolute(contract.validTo) : '—'}
                  </span>
                ),
              },
              {
                key: 'licReq',
                label: 'License required',
                children: contract.licenseRequired ? (
                  <span style={{ color: 'var(--ol-attention)', fontWeight: 600, fontSize: 12.5 }}>
                    Yes — go-live needs an ACTIVE license
                  </span>
                ) : (
                  <span style={{ fontSize: 12.5 }}>No</span>
                ),
              },
              {
                key: 'invRef',
                label: 'Invoicing account',
                children: contract.invoicingAccountRef ? (
                  <span className="ol-mono" style={{ fontSize: 12 }}>{contract.invoicingAccountRef}</span>
                ) : (
                  <Text type="secondary">— not wired</Text>
                ),
              },
              ...(contract.fees?.length
                ? [
                    {
                      key: 'fees',
                      label: 'Fees',
                      children: (
                        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
                          {contract.fees.map((f) => (
                            <Tag key={f.key} style={{ margin: 0, fontSize: 11 }}>
                              {f.key}: {formatMicros(f.amountMicros, contract.currency)} ({f.recurrence})
                            </Tag>
                          ))}
                        </span>
                      ),
                    },
                  ]
                : []),
            ]}
          />

          {detailLoading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : (
            <>
              {wiring && (
                <div className="ol-card" style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <SafetyCertificateOutlined aria-hidden style={{ color: 'var(--ol-primary)' }} />
                    <Text strong style={{ fontSize: 13 }}>
                      Billing wiring <InfoTip surface="admin" field="Billing wiring" />
                    </Text>
                    <StatusTag value={wiring.status} />
                    {wiring.evaluatedAt && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        evaluated {formatAbsolute(wiring.evaluatedAt)}
                      </Text>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                    <WiringFact ok={wiring.contractActive} label="Contract active" />
                    <WiringFact ok={wiring.meterBound} label="Meter bound" />
                    <WiringFact ok={wiring.ratingBound} label="Rating bound" />
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
                      {wiring.licenseState === 'ACTIVE' ? (
                        <CheckCircleFilled aria-hidden style={{ color: 'var(--ol-positive)' }} />
                      ) : (
                        <MinusCircleOutlined aria-hidden style={{ color: 'var(--ol-text-tertiary)' }} />
                      )}
                      License: {wiring.licenseState ?? '—'}
                    </span>
                    <WiringFact ok={!!wiring.invoicingHandshakeAt} label="Invoicing handshake" />
                  </div>
                  {!!wiring.reasons?.length && (
                    <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12, color: 'var(--ol-text-secondary)' }}>
                      {wiring.reasons.map((r) => (
                        <li key={r}>{r}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <Title level={5} style={{ margin: '0 0 4px', fontSize: 14 }}>
                Rate card &amp; metered consumption
              </Title>
              <Paragraph type="secondary" style={{ margin: '0 0 8px', fontSize: 12.5, maxWidth: 760 }}>
                Each row binds a metering unit from the closed meter catalogue to a contracted rate.
                &ldquo;Consumed&rdquo; is the latest rated period&apos;s quantity — a dash means no
                rated period covers that unit yet (never a fabricated zero). Live operational usage is
                on the Metering tab.
              </Paragraph>
              <Table<UnitRow>
                rowKey="unitKey"
                size="small"
                columns={unitColumns}
                dataSource={unitRows}
                pagination={false}
                style={{ marginBottom: 16 }}
                locale={{ emptyText: 'No rate lines on this contract yet — the meter is not bound.' }}
              />

              <Title level={5} style={{ margin: '0 0 4px', fontSize: 14 }}>
                Licenses
              </Title>
              <Table<LicenseView>
                rowKey="licenseId"
                size="small"
                columns={licenseColumns}
                dataSource={licenses ?? []}
                pagination={false}
                style={{ marginBottom: 16 }}
                locale={{
                  emptyText: contract.licenseRequired
                    ? 'No license issued yet — this contract requires one before go-live.'
                    : 'No license issued (this contract does not require one).',
                }}
              />

              <Title level={5} style={{ margin: '0 0 4px', fontSize: 14 }}>
                Rating runs
              </Title>
              <Table<RatingView>
                rowKey={(r) => `${r.periodStart ?? ''}-${r.periodEnd ?? ''}-${r.ratedAt ?? ''}`}
                size="small"
                columns={ratingColumns}
                dataSource={ratings ?? []}
                pagination={ratings && ratings.length > 10 ? { pageSize: 10 } : false}
                locale={{ emptyText: 'No rated periods yet.' }}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
